<template>
    <div v-cloak>
        <v-card id="courses" elevation="4" class="mb-8">
            <v-toolbar color="purple" dark flat dense>
                <v-toolbar-title>{{ enrolment.CourseName }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-scroll-to="'#top'" dark text>
                    <v-icon left>fas fa-chevron-up</v-icon>
                    Top
                </v-btn>
                <span v-if="IsEnrolmentRegistered"
                      class="fa-stack fa-md icon-info">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-info fa-stack-1x"></i>
                </span>
                <span v-else-if="IsRegistrationComplete"
                      class="fa-stack fa-md icon-success">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-check fa-stack-1x"></i>
                </span>
                <span v-else
                      class="fa-stack fa-md icon-error">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-exclamation fa-stack-1x"></i>
                </span>
            </v-toolbar>
            <v-card-text>
                <p class="text-h6">Register for your course</p>
                <v-alert v-if="IsEnrolmentRegistered" type="info" outlined dense text>You are registered on this course</v-alert>
                <v-alert v-if="!IsEnrolmentRegistered && !IsRegistrationStarted" type="error" outlined dense text>You have not started registration for this course yet</v-alert>
                <v-alert v-if="!IsEnrolmentRegistered && IsRegistrationInProgress" type="error" outlined dense text>You have not completed registration for this course yet</v-alert>
                <v-alert v-if="!IsEnrolmentRegistered && IsRegistrationComplete" type="success" outlined dense text>Your registration is complete on this course</v-alert>

                <v-row>
                    <v-col>
                        <table border="0"
                               cellpadding="0"
                               cellspacing="5"
                               class="my-4">
                            <tr>
                                <th width="150" align="left">Course Code:</th>
                                <th align="left">{{ enrolment.CourseCode }}</th>
                            </tr>
                            <tr>
                                <td>Campus:</td>
                                <td>{{ enrolment.CampusName }}</td>
                            </tr>
                            <tr>
                                <td>Level:</td>
                                <td>{{ enrolment.SQAQualificationLevel }}</td>
                            </tr>
                            <tr>
                                <td>Mode:</td>
                                <td>{{ enrolment.IsFullTime ? 'Full Time' : 'Part Time' }}</td>
                            </tr>
                            <tr>
                                <td>Start Date:</td>
                                <td>{{ enrolment.StartDate | formatLongDate }}</td>
                            </tr>
                        </table>
                    </v-col>
                    <v-col v-if="!!enrolmentRegistration.lastUpdated">
                        <table border="0"
                               cellpadding="0"
                               cellspacing="5"
                               class="my-4">
                            <tr v-if="!enrolmentRegistration.hasCompleted">
                                <td width="240">Last Registration Section Viewed:</td>
                                <td>{{ enrolmentRegistration.lastSectionViewed }}</td>
                            </tr>
                            <tr>
                                <td>Registration Last Updated:</td>
                                <td>{{ enrolmentRegistration.lastUpdated | formatDateTime }}</td>
                            </tr>
                        </table>
                    </v-col>
                </v-row>

                <p v-if="HasInductionEvents" class="mb-0">View your induction information and more information on your course:</p>
                <p v-else class="mb-0">View more information on your course:</p>
            </v-card-text>
            <v-card-actions class="pl-4">
                <v-row v-if="withdrawnApplication.applicationWithdrawn">
                    <v-col sm="auto" class="px-1">
                        <v-alert type="warning" outlined dense text :value="true">You have withdrawn your application for this course on {{withdrawnApplication.lastUpdated | formatDateTime}}.</v-alert>
                    </v-col>
                </v-row>
                <v-row v-else>
                    <v-col v-if="!IsEnrolmentRegistered && !IsRegistrationStarted" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="error" dark small style="color:white !important">
                            <v-icon left small>fas fa-pencil-alt</v-icon>
                            Click to Register
                        </v-btn>
                    </v-col>
                    <v-col v-if="IsRegistrationInProgress" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="error" dark small style="color:white !important">
                            <v-icon left small>fas fa-tasks</v-icon>
                            Continue Registration
                        </v-btn>
                    </v-col>
                    <v-col v-if="IsRegistrationComplete" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="success" dark small style="color:white !important">
                            <v-icon left small>fas fa-check</v-icon>
                            Registration Complete
                        </v-btn>
                    </v-col>
                    <v-col v-if="(!IsEnrolmentRegistered && !IsRegistrationStarted) || IsRegistrationInProgress" sm="auto" class="px-1">
                        <v-btn @click="ConfirmApplicationWithdrawal" color="secondary" :loading="loadingWithdrawnApplication || submittedWithdrawnApplication" dark small style="color:white !important">
                            <v-icon left small>fas fa-times</v-icon>
                            Withdraw my Application
                        </v-btn>
                    </v-col>
                    <v-col v-if="loadingInductionEvents || HasInductionEvents(enrolment.CourseCode)" sm="auto" class="px-1">
                        <v-skeleton-loader v-if="loadingInductionEvents" type="button" class="mx-2"></v-skeleton-loader>
                        <v-btn v-if="HasInductionEvents(enrolment.CourseCode)" @click="OpenCourseInduction" color="info" dark small>
                            <v-icon left small>far fa-calendar-alt</v-icon>
                            Your Induction
                        </v-btn>
                    </v-col>
                    <v-col sm="auto" class="px-1">
                        <v-btn @click="OpenCourseInformation" color="info" dark small>
                            <v-icon left small>fas fa-book</v-icon>
                            Course and Staff Information
                        </v-btn>
                    </v-col>
                    <v-col sm="auto" class="px-1">
                        <v-btn @click="OpenVideoGuidance" color="info" dark small>
                            <v-icon left small>fas fa-info-circle</v-icon>
                            Registration Guidance (Video)
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>

        <confirm-dialog instance="withdrawn-application"
                        title="Confirm withdrawal of application"
                        question="Your application will now be withdrawn, do you wish to proceed?"
                        no-label="No"
                        yes-label="Yes"
                        button-color="error"></confirm-dialog>
    </div>
</template>

<script>
    import { eventBus } from '@/main'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'
    import timetableEventsMixin from '@/views/components/mixins/enterpriseDataApi/timetableEvents.js'
    import enrolmentRegistrationsMixin from '@/views/components/mixins/enrolmentRegistrations.js'
    import withdrawnApplicationMixin from '@/views/components/mixins/withdrawnApplication.js'
    import studentsMixin from '@/views/components/mixins/enterpriseDataApi/students.js'

    import confirmDialog from '@/views/components/dialogs/ConfirmDialog.vue'

    export default {
        props: ['enrolment', 'enrolmentRegistration'],
        components: {
            'confirm-dialog': confirmDialog,
        },
        mixins: [
            filtersMixin,
            appApiMixin,
            timetableEventsMixin,
            enrolmentRegistrationsMixin,
            withdrawnApplicationMixin,
            studentsMixin,
        ],
        data() {
            return {
                loadingWithdrawnApplication: false,
                submittedWithdrawnApplication: false,
            }
        },
        computed: {
            IsEnrolmentRegistered() { return ['L', 'R'].indexOf(this.enrolment.EnrolmentStatusCode) > -1; },
        },
        mounted() {
            eventBus.$off(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:started`).$on(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:started`, () => this.loadingWithdrawnApplication = true);
            eventBus.$off(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:finished`).$on(`withdrawn-application-fetch:${this.enrolment.EnrolmentID}:finished`, () => this.loadingWithdrawnApplication = false);
            eventBus.$off(`withdrawn-application-save:${this.enrolment.EnrolmentID}:started`).$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:started`, () => this.submittedWithdrawnApplication = true);
            eventBus.$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:success`, response => {
                eventBus.$emit('alert:success', response);
                this.LoadWithdrawnApplication(this.enrolment.EnrolmentID);
            });
            eventBus.$off(`withdrawn-application-save:${this.enrolment.EnrolmentID}:finished`).$on(`withdrawn-application-save:${this.enrolment.EnrolmentID}:finished`, () => this.submittedWithdrawnApplication = false);

            this.LoadTimetableEvents(this.enrolment.CourseCode);
            this.LoadInductionEvents(this.enrolment.CourseCode);
            this.LoadWithdrawnApplication(this.enrolment.EnrolmentID);
            //this.LoadStudent();
        },
        methods: {
            OpenRegistration() {
                let path = `/enrolment/${this.enrolment.EnrolmentID}/${this.enrolment.AcademicSession}`;
                if (this.enrolment.OnboardingTypeCode == 'Short') path += '/short';

                let lastSectionViewed = this.TranslatePageToPath(this.enrolmentRegistration?.lastSectionViewed);

                if (lastSectionViewed != null) path += lastSectionViewed;

                this.$router.push({ path: path });
            },
            OpenCourseInduction() {
                this.$router.push({ path: `/course/${this.enrolment.CourseCode}/induction` });
            },
            OpenCourseInformation() {
                this.$router.push({ path: `/course/${this.enrolment.CourseCode}/information` });
            },
            OpenVideoGuidance() {
                window.open('https://web.microsoftstream.com/video/9c3ee033-aafe-42ea-9b4e-d93de250457d', '_blank');
            },
            ConfirmApplicationWithdrawal() {
                eventBus.$off('withdrawn-application:ok').$on('withdrawn-application:ok', () => this.WithdrawApplication());
                eventBus.$emit('withdrawn-application:show');
            },
            WithdrawApplication() {
                var withdrawnApplication = {
                    enrolmentID: this.enrolment.EnrolmentID,
                    applicationWithdrawn: true,
                    Username: this.student.StudentReference,
                    Displayname: this.StudentDisplayName
                }
                this.SaveWithdrawnApplication(withdrawnApplication);
            }
        },
    }
</script>